import { FC, useMemo } from 'react';

import { Card, CardHeadingDefaultProps, CardProps, Paragraph } from '@purpur/library';
import { IconArrowRight } from '@purpur/library/icon/arrow-right';

import { useTranslations } from '@telialt/common-cms/src/useTranslations';
import { formatDate } from '@telialt/utils/src/helpers';

import { AuthorBadge } from '../common/AuthorBadge/AuthorBadge';
import { ContentfulImage } from '../common/ContentfulImage';
import { CardDate, CardDetailsContainer, CardMediaLayout } from './ArticleCard.styles';
import { MediaFallback } from './MediaFallback';
import { ArticleCardData } from './model';

type Inheritables = Omit<CardProps, 'title' | 'children' | 'content' | 'variant'>;
type Props = {
    variant?: CardProps['variant'];
    headingTag?: CardHeadingDefaultProps['headingTag'];
    content: ArticleCardData;
} & Inheritables;

export const ArticleCard: FC<Props> = ({ variant, headingTag, content, ...inherited }) => {
    const { t } = useTranslations();

    const author = useMemo(() => {
        if (!content.authors?.length) {
            return null;
        }

        const isMultipleAuthors = content.authors?.length > 1;

        return {
            isMultipleAuthors,
            image: content.authorsImage?.image || content.authors[0]?.image,
            description: content.authorsImage?.description || content.authors[0]?.description,
            label: isMultipleAuthors ? t['blog.article_card.multiple_authors'] : content.authors[0]?.title,
        };
    }, [content.authors, content.authorsImage?.description, content.authorsImage?.image, t]);

    const bannerImage = content.bannerImage;
    const banner = useMemo(() => {
        if (!bannerImage?.image) {
            return <MediaFallback />;
        } else {
            return (
                <ContentfulImage
                    alt={bannerImage.description || ''}
                    src={bannerImage.image}
                    width={bannerImage.width}
                    height={bannerImage.height}
                    style={{ width: '100%', height: 'auto' }}
                />
            );
        }
    }, [bannerImage]);

    return (
        <Card variant={variant} data-element="blog-card" data-element-name={content.title} {...inherited}>
            <CardMediaLayout>
                <Card.Media>{banner}</Card.Media>
                <Card.ContentContainer gapSize="sm">
                    <CardDetailsContainer>
                        {!!content.category && <Card.Badge text={content.category} variant="information" />}
                        <CardDate>{formatDate(content.effectiveDate)}</CardDate>
                    </CardDetailsContainer>
                    <Card.Heading title={content.title} headingTag={headingTag} href={content.href}>
                        <Card.Cta linkStyling={true}>
                            <IconArrowRight size="sm" />
                        </Card.Cta>
                    </Card.Heading>
                    <Paragraph>{content.summary}</Paragraph>

                    {!!author && (
                        <AuthorBadge
                            image={author.image}
                            description={author.description}
                            name={author.label}
                            isMultipleAuthors={author.isMultipleAuthors}
                        />
                    )}
                </Card.ContentContainer>
            </CardMediaLayout>
        </Card>
    );
};
